import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, ShieldCheck, Loader2 } from 'lucide-react';
import { login } from '../api/studentApi';
import './LoginComponent.css';
import { useUser } from '../context/UserContext';
import { trackEvent } from '../utils/analytics';
import { GoogleLogin } from '@react-oauth/google';
import { googleLogin } from '../api/studentApi';

const LoginComponent = ({ onClickShowForgotPassword }) => {
    const navigate = useNavigate();
    const [credentials, setCredentials] = useState({ identifier: '', password: '' });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { loginUser } = useUser(); // Access the loginUser function from context
    const [errorMessage, setErrorMessage] = useState(null);

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            setErrorMessage(null);

            const res = await googleLogin(credentialResponse.credential);

            const data = res.data;

            // Store JWT token & update auth context
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));

            loginUser(data.user);
            trackEvent('login', { role: data.user.role }); // Track login event
            // Redirect to dashboard
            navigate('/parent-dashboard');
        } catch (err) {
            setErrorMessage(err.message);
        }
    };

    const handleGoogleFailure = () => {
        setErrorMessage('Google Authentication was cancelled or failed.');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await login(credentials);
            const { token, user } = response.data;

            // Store auth data
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            loginUser(user);
            trackEvent('login', { role: user.role }); // Track login event

            // Redirect based on role
            if (user.role === 'parent') {
                navigate('/parent-dashboard');
            } else if (user.role === 'school') {
                navigate('/school-dashboard');
            } else {
                navigate('/student/home');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Invalid username/email or password');
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <div className="login-card">
            <div className="login-header">
                <div className="login-icon-circle">
                    <ShieldCheck size={40} color="#4f46e5" />
                </div>
                <h2>Welcome Back!</h2>
                <p>Login to continue your learning journey</p>
            </div>

            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleLogin} className="login-form">
                <div className="input-group">
                    <Mail className="input-icon" size={20} />
                    <input
                        type="text"
                        placeholder="Username or Email"
                        required
                        onChange={(e) => setCredentials({ ...credentials, identifier: e.target.value })}
                    />
                </div>

                <div className="input-group">
                    <Lock className="input-icon" size={20} />
                    <input
                        type="password"
                        placeholder="Password"
                        required
                        onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    />
                </div>

                <div className="forgot-pass">
                    <button type="button" className="link-button" onClick={onClickShowForgotPassword}>
                        Forgot Password?
                    </button>
                </div>

                <button type="submit" className="btn primary-btns btn-full">
                    {isLoading ? (
                        <>Authenticating... <Loader2 size={20} className="animate-spin" /></>
                    ) : (
                        <>Login</>
                    )}
                </button>
                <div className="google-login-wrapper">
                    <div className="google-login-section">
                        <span className="google-login-text">Only for parents</span>
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={handleGoogleFailure}
                            theme="outline"
                            size="large"
                            text="continue_with"
                            shape="rectangular"
                            className="google-login-button"
                        />
                    </div>
                </div>
            </form>

            <div className="login-footer">
                <p>Don't have an account? <Link to="/register">Join Now</Link></p>
            </div>
        </div>
    );
};

const styles = {
    card: {
        maxWidth: '400px',
        margin: '60px auto',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
        textAlign: 'center',
        background: '#ffffff',
    },
    buttonWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
    },
    error: {
        color: '#ef4444',
        fontSize: '14px',
        marginTop: '8px',
    },
};

export default LoginComponent;